import { EmailMetadata } from '@/types';
import { generateEmailFileName } from '@/config';
import { authService } from './authService';
import { logger } from '@/utils/logger';

class EmailService {
  // ==================== EMAIL OPERATIONS ====================

  // Generate a stable hash for duplicate detection based on email identity

  // Get current email metadata from Outlook
  async getCurrentEmailMetadata(): Promise<EmailMetadata | null> {
    return new Promise((resolve) => {
      const item = Office.context.mailbox?.item;

      if (!item) {
        resolve(null);
        return;
      }

      const metadata: EmailMetadata = {
        subject: item.subject || '(No Subject)',
        from: '',
        to: [],
        date: new Date(),
        hasAttachments: false,
      };

      if (item.from) {
        metadata.from = item.from.emailAddress || item.from.displayName || '';
      }

      if (item.to) {
        item.to.forEach((recipient: Office.EmailAddressDetails) => {
          metadata.to.push(recipient.emailAddress || recipient.displayName || '');
        });
      }

      if (item.cc && item.cc.length > 0) {
        metadata.cc = [];
        item.cc.forEach((recipient: Office.EmailAddressDetails) => {
          metadata.cc!.push(recipient.emailAddress || recipient.displayName || '');
        });
      }

      if (item.dateTimeCreated) {
        metadata.date = item.dateTimeCreated;
      }

      if (item.conversationId) {
        metadata.conversationId = item.conversationId;
      }

      if (item.internetMessageId) {
        metadata.internetMessageId = item.internetMessageId;
      }

      if (item.attachments && item.attachments.length > 0) {
        metadata.hasAttachments = true;
      }

      item.body.getAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          metadata.bodyPreview = result.value.slice(0, 500);
        }
        resolve(metadata);
      });
    });
  }

  /**
   * Fetch a specific message (by Outlook item ID) as EML via Microsoft Graph,
   * using the NAA delegated token. Used by multi-select save.
   *
   * The legacy Office callback-token path (getCallbackTokenAsync isRest) targeted
   * the Outlook REST API (outlook.office.com/api), which Microsoft has retired -
   * it now fails with "Failed to get callback token: An internal error has
   * occurred." Graph's /messages/{id}/$value returns the same MIME (.eml).
   */
  async getEmailAsEmlByItemId(
    itemId: string,
    subject?: string,
    fallbackDate?: Date,
  ): Promise<{ content: string; fileName: string; contentHash: string; receivedDate?: Date }> {
    const token = await authService.getAccessToken(['Mail.Read']);

    // Office item ids are EWS ids; Graph accepts the REST v2.0 immutable id, so
    // convert when the host supports it (it does in new Outlook / OWA).
    let messageId = itemId;
    const mailbox = Office.context.mailbox as any;
    if (typeof mailbox?.convertToRestId === 'function') {
      try {
        messageId = mailbox.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0);
      } catch (e) {
        logger.debug('Could not convert item id to REST id:', e);
      }
    }

    let receivedDate = this.validDate(fallbackDate);
    try {
      const metadataResponse = await fetch(
        `https://graph.microsoft.com/v1.0/me/messages/${encodeURIComponent(messageId)}?$select=receivedDateTime,createdDateTime`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (metadataResponse.ok) {
        const message = (await metadataResponse.json()) as {
          receivedDateTime?: string;
          createdDateTime?: string;
        };
        receivedDate =
          this.validDate(message.receivedDateTime) ||
          this.validDate(message.createdDateTime) ||
          receivedDate;
      }
    } catch (error) {
      // Received-date metadata is additive; MIME filing must still proceed.
      logger.debug('Could not read Graph message timestamps:', error);
    }

    const response = await fetch(
      `https://graph.microsoft.com/v1.0/me/messages/${encodeURIComponent(messageId)}/$value`,
      { headers: { Authorization: `Bearer ${token}`, Accept: 'message/rfc822' } },
    );
    if (!response.ok) {
      throw new Error(`Failed to get email: ${response.status} ${response.statusText}`);
    }
    const content = await response.text();

    const fileName = generateEmailFileName(subject || 'No Subject', 'eml');
    const contentHash = await this.generateContentHash(content);
    return { content, fileName, contentHash, receivedDate };
  }

  async getReceivedDateFromFile(fileName: string, data: ArrayBuffer): Promise<Date | undefined> {
    const lowerName = fileName.toLowerCase();
    if (lowerName.endsWith('.eml')) {
      return this.validDate(this.parseEmlMetadata(new TextDecoder().decode(data)).date);
    }
    if (lowerName.endsWith('.msg')) {
      const parsed = await this.parseEmailPreview(data, 'msg');
      return this.validDate(parsed.metadata.date);
    }
    return undefined;
  }

  private validDate(value?: Date | string | null): Date | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isFinite(date.getTime()) && date.getTime() > 0 ? date : undefined;
  }

  /**
   * Parse attachment presence once while an email source is already in memory.
   * Callers decide whether a parse failure means unknown; this method never
   * downloads a filed message and never exposes attachment names.
   */
  async detectEmailAttachments(data: ArrayBuffer, kind: 'eml' | 'msg'): Promise<boolean> {
    if (kind === 'msg') {
      const { default: MsgReader } = await import('@kenjiuno/msgreader');
      const msg = new MsgReader(data).getFileData() as {
        attachments?: Array<{ pidContentId?: string }>;
      };
      return (msg.attachments || []).some((attachment) => !attachment.pidContentId);
    }

    const { default: PostalMime } = await import('postal-mime');
    const parsed = await new PostalMime().parse(data);
    return (parsed.attachments || []).some((attachment) => attachment.disposition !== 'inline');
  }

  // Parsed, render-ready email preview. html (when present) is the message's
  // real HTML body with cid: inline images resolved to data URLs - rendered
  // by the preview panel in a sandboxed iframe. text is the plain fallback.
  async parseEmailPreview(
    data: ArrayBuffer,
    kind: 'eml' | 'msg',
  ): Promise<{ metadata: EmailMetadata; html?: string; text?: string }> {
    if (kind === 'msg') {
      const { default: MsgReader } = await import('@kenjiuno/msgreader');
      const reader = new MsgReader(data);
      const msg = reader.getFileData() as {
        subject?: string;
        senderName?: string;
        senderEmail?: string;
        recipients?: Array<{ name?: string; email?: string }>;
        body?: string;
        bodyHtml?: string;
        html?: Uint8Array;
        messageDeliveryTime?: string;
        attachments?: Array<{ pidContentId?: string; attachMimeTag?: string }>;
      };
      const metadata: EmailMetadata = {
        subject: msg.subject || '',
        from: msg.senderEmail || msg.senderName || '',
        to: (msg.recipients || []).map((r) => r.email || r.name || '').filter(Boolean),
        cc: [],
        date: msg.messageDeliveryTime ? new Date(msg.messageDeliveryTime) : new Date(0),
        hasAttachments: (msg.attachments || []).length > 0,
      };

      // Prefer the HTML body (PidTagBodyHtml string, else PidTagHtml bytes);
      // RTF-only messages fall back to the plain-text body.
      let html = msg.bodyHtml || undefined;
      if (!html && msg.html && msg.html.length > 0) {
        html = new TextDecoder('utf-8').decode(msg.html);
      }
      if (html) {
        // Resolve inline cid: images from the msg attachments.
        (msg.attachments || []).forEach((att, index) => {
          if (!att.pidContentId || !att.attachMimeTag?.startsWith('image/')) return;
          try {
            const content = reader.getAttachment(index).content as Uint8Array;
            let binary = '';
            for (let i = 0; i < content.length; i++) binary += String.fromCharCode(content[i]);
            const dataUrl = `data:${att.attachMimeTag};base64,${btoa(binary)}`;
            html = (html as string).split(`cid:${att.pidContentId}`).join(dataUrl);
          } catch {
            // attachment not readable - leave the cid reference
          }
        });
      }
      return { metadata, html, text: msg.body || '' };
    }

    const { default: PostalMime } = await import('postal-mime');
    const parsed = await new PostalMime().parse(data);
    const metadata: EmailMetadata = {
      subject: parsed.subject || '',
      from: parsed.from?.address || parsed.from?.name || '',
      to: (parsed.to || []).map((a) => a.address || a.name || '').filter(Boolean),
      cc: (parsed.cc || []).map((a) => a.address || a.name || '').filter(Boolean),
      date: parsed.date ? new Date(parsed.date) : new Date(0),
      hasAttachments: (parsed.attachments || []).some((a) => a.disposition !== 'inline'),
    };

    let html = parsed.html || undefined;
    if (html) {
      // Resolve inline cid: images from the attachments so they display.
      for (const att of parsed.attachments || []) {
        if (!att.contentId || !att.mimeType?.startsWith('image/')) continue;
        const cid = att.contentId.replace(/[<>]/g, '');
        const bytes = new Uint8Array(att.content as ArrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const dataUrl = `data:${att.mimeType};base64,${btoa(binary)}`;
        html = html.split(`cid:${cid}`).join(dataUrl);
      }
    }
    return { metadata, html, text: parsed.text || undefined };
  }

  parseEmlMetadata(emlContent: string): EmailMetadata {
    const headers: Record<string, string> = {};
    const lines = emlContent.split(/\r?\n/);
    let currentHeader = '';

    for (const line of lines) {
      if (line.trim() === '') break;

      if (line.startsWith(' ') || line.startsWith('\t')) {
        if (currentHeader) {
          headers[currentHeader] += ' ' + line.trim();
        }
        continue;
      }

      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        currentHeader = line.slice(0, colonIndex).toLowerCase();
        headers[currentHeader] = line.slice(colonIndex + 1).trim();
      }
    }

    const parseAddresses = (value: string): string[] => {
      if (!value) return [];
      return value
        .split(',')
        .map((addr) => {
          const match = addr.match(/<([^>]+)>/) || [null, addr.trim()];
          return match[1] || addr.trim();
        })
        .filter(Boolean);
    };

    return {
      subject: headers['subject'] || '(No Subject)',
      from: parseAddresses(headers['from'])[0] || '',
      to: parseAddresses(headers['to']),
      cc: headers['cc'] ? parseAddresses(headers['cc']) : undefined,
      date: headers['date'] ? new Date(headers['date']) : new Date(0),
      hasAttachments: emlContent.includes('Content-Disposition: attachment'),
      messageId: headers['message-id'],
      internetMessageId: headers['message-id'],
    };
  }

  // Parse body content from EML
  parseEmlBody(emlContent: string): string {
    // Find the body after headers (double newline)
    const headerEndIndex = emlContent.search(/\r?\n\r?\n/);
    if (headerEndIndex === -1) return '';

    let body = emlContent.slice(headerEndIndex).trim();

    // Check for multipart content
    const boundaryMatch = emlContent.match(/boundary="?([^"\r\n]+)"?/i);
    if (boundaryMatch) {
      const boundary = boundaryMatch[1];
      const parts = body.split(new RegExp(`--${boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`));

      // Find text/plain part first, then text/html
      let textContent = '';
      let htmlContent = '';

      for (const part of parts) {
        if (part.includes('Content-Type: text/plain')) {
          const contentStart = part.search(/\r?\n\r?\n/);
          if (contentStart !== -1) {
            textContent = part.slice(contentStart).trim();
          }
        } else if (part.includes('Content-Type: text/html')) {
          const contentStart = part.search(/\r?\n\r?\n/);
          if (contentStart !== -1) {
            htmlContent = part.slice(contentStart).trim();
          }
        }
      }

      // Prefer text content, fall back to stripping HTML
      if (textContent) {
        body = this.decodeQuotedPrintable(textContent);
      } else if (htmlContent) {
        body = this.stripHtml(this.decodeQuotedPrintable(htmlContent));
      }
    }

    // Decode quoted-printable if present
    if (emlContent.includes('Content-Transfer-Encoding: quoted-printable')) {
      body = this.decodeQuotedPrintable(body);
    }

    return body.trim();
  }

  // Decode quoted-printable encoding
  private decodeQuotedPrintable(str: string): string {
    return str
      .replace(/=\r?\n/g, '') // Remove soft line breaks
      .replace(/=([0-9A-Fa-f]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  }

  // Strip HTML tags
  private stripHtml(html: string): string {
    return html
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/\s+/g, ' ')
      .trim();
  }

  async generateContentHash(content: string | ArrayBuffer): Promise<string> {
    let data: BufferSource;
    if (typeof content === 'string') {
      data = new TextEncoder().encode(content);
    } else {
      data = content;
    }
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  // The currently open message in read mode, as a ref the save service can
  // resolve into .eml bytes. Returns null in compose mode (no itemId) or when
  // no message is open. Used by the mobile "save mode".
  getReadItemRef(): { itemId: string; subject: string; date?: Date } | null {
    if (typeof Office === 'undefined' || !Office.context?.mailbox?.item) {
      return null;
    }
    const item = Office.context.mailbox.item as any;
    // Read mode exposes a string itemId; compose mode does not.
    if (typeof item.itemId !== 'string' || !item.itemId) {
      return null;
    }
    return {
      itemId: item.itemId,
      subject: typeof item.subject === 'string' && item.subject ? item.subject : '(No Subject)',
      date: item.dateTimeCreated ? new Date(item.dateTimeCreated) : undefined,
    };
  }

  // ==================== COMPOSE MODE - ATTACH FILES ====================

  // Check if currently in compose mode
  isComposeMode(): boolean {
    if (typeof Office === 'undefined' || !Office.context?.mailbox?.item) {
      return false;
    }
    const item = Office.context.mailbox.item as any;
    // Compose mode has setAsync methods on subject/to/body
    return typeof item.subject?.setAsync === 'function' || typeof item.to?.setAsync === 'function';
  }

  // Attach a file to the current compose email
  async attachFile(
    fileName: string,
    base64Content: string,
    _contentType: string,
  ): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.isComposeMode()) {
        logger.warn('Cannot attach file: not in compose mode');
        resolve(false);
        return;
      }

      const item = Office.context.mailbox.item as Office.MessageCompose;

      item.addFileAttachmentFromBase64Async(
        base64Content,
        fileName,
        { isInline: false },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            logger.debug('File attached successfully:', fileName);
            resolve(true);
          } else {
            logger.error('Failed to attach file:', result.error);
            resolve(false);
          }
        },
      );
    });
  }

  // Attach multiple files
  async attachFiles(
    files: Array<{ fileName: string; base64Content: string; contentType: string }>,
  ): Promise<{
    success: string[];
    failed: string[];
  }> {
    const success: string[] = [];
    const failed: string[] = [];

    for (const file of files) {
      const result = await this.attachFile(file.fileName, file.base64Content, file.contentType);
      if (result) {
        success.push(file.fileName);
      } else {
        failed.push(file.fileName);
      }
    }

    return { success, failed };
  }
}

export const emailService = new EmailService();
